// Константы схемы документа (совпадают с web/server/stoweb/model.py и writer/builder.py).

/** Структурные элементы: вид -> заголовок (writer распознаёт элемент по тексту заголовка). */
export const STRUCT_KINDS: Record<string, string> = {
  intro: 'Введение',
  // СТО 2026, 3.5.1: единственный элемент «Определения, обозначения и сокращения»; terms/abbrev - виды старых
  // проектов (при открытии переводятся в defs), выгружаются тем же элементом
  defs: 'Определения, обозначения и сокращения',
  terms: 'Определения, обозначения и сокращения',
  abbrev: 'Определения, обозначения и сокращения',
  normrefs: 'Нормативные ссылки',
  conclusions: 'Выводы',
  conclusion: 'Заключение',
  sources: 'Список использованных источников',
};
/** 3.5.2: вводная фраза перечня определений, обозначений и сокращений. */
export const DEFS_INTRO = 'В настоящем текстовом документе применяются следующие определения, обозначения и сокращения:';
/** Заголовки, которые сводятся к «Определения, обозначения и сокращения» (вставка, импорт, «Сделать структурным»). */
export const DEFS_ALIASES = ['термины и определения', 'определения', 'перечень сокращений и обозначений', 'перечень сокращений',
  'список сокращений', 'обозначения и сокращения', 'сокращения', 'сокращения и обозначения', 'перечень условных обозначений',
  'перечень сокращений, условных обозначений, символов, единиц и терминов', 'определения, обозначения и сокращения'];

export const APP_STATUSES = ['обязательное', 'рекомендуемое', 'справочное'] as const;
/** Буквы приложений (4.16.4): без Ё, З, Й, О, Ч, Ъ, Ы, Ь (и Е). */
export const APP_LETTERS = 'АБВГДЖИКЛМНПРСТУФХЦШЩЭЮЯ';
/** Буквы перечислений (4.4.4): без е, ё, з, й, о, ч, ъ, ы, ь. */
export const LIST_LETTERS = 'абвгджиклмнпрстуфхцшщэюя';

/** Узлы, у которых есть атрибут id (блоки и сноски). */
export const ID_TYPES = ['paragraph', 'heading', 'structHeading', 'stoList', 'figure', 'stoTable', 'formula', 'listing',
  'note', 'example', 'pageBreak', 'appendix', 'footnote'];

/** Вид метки writer для перекрёстной ссылки на узел. */
export const XREF_KIND: Record<string, 'fig' | 'tbl' | 'eq' | 'lst' | 'app' | 'sec'> = {
  figure: 'fig', stoTable: 'tbl', formula: 'eq', listing: 'lst', appendix: 'app', heading: 'sec', structHeading: 'sec',
};

export const EN_DASH = '–';

/** Неразрывные пробелы в тексте отчёта - переключатель, как lib/sto_lib.USE_NBSP (по умолчанию ВЫКЛЮЧЕНЫ): Word
 *  показывает их кружками «°» в режиме знаков форматирования и не растягивает при выравнивании по ширине. Пока
 *  выключено, типографика, подписи, ссылки, инициалы и вставка ставят обычный пробел (U+0020) и обычный дефис, а
 *  пришедшие извне неразрывные (вставка, импорт, старые проекты) заменяются обычными. Включить - вернуть true
 *  (вместе с sto_lib.USE_NBSP: разметка страницы и экспорт должны совпадать). */
export const USE_NBSP = false;
/** Сам символ неразрывного пробела - только для распознавания (регулярные выражения, показ знаков форматирования). */
export const NBSP_CHAR = '\u00a0';
/** Пробел там, где типографика ставит «неразрывный»: U+00A0 при USE_NBSP, иначе обычный пробел. */
export const NBSP = USE_NBSP ? NBSP_CHAR : ' ';
/** Неразрывные пробелы (обычный, узкий, цифровой) и неразрывный дефис - то, что убирается при USE_NBSP = false. */
const NB_SPACES_RE = /[\u00a0\u202f\u2007]/g;
const NB_HYPHEN_RE = /\u2011/g;

/** Строка без неразрывных пробелов и дефисов (при USE_NBSP - как есть). */
export function plainSpaces(s: string): string {
  if (USE_NBSP || !s) return s;
  return s.replace(NB_SPACES_RE, ' ').replace(NB_HYPHEN_RE, '-');
}

/** Есть ли в строке символы, которые plainSpaces заменит. */
export function hasNbsp(s: string): boolean {
  return !USE_NBSP && !!s && /[\u00a0\u202f\u2007\u2011]/.test(s);
}

/** Документ/фрагмент TipTap JSON (или метаданные, источники) без неразрывных пробелов: текст узлов, строковые
 *  атрибуты (код листинга, LaTeX, место в источнике), содержимое сносок - глубоко. Тот же объект, если менять нечего. */
export function plainSpacesDeep<T>(v: T): T {
  if (USE_NBSP) return v;
  const walk = (x: any): any => {
    if (typeof x === 'string') return hasNbsp(x) ? plainSpaces(x) : x;
    if (Array.isArray(x)) {
      let out: any[] | null = null;
      x.forEach((e, i) => { const y = walk(e); if (y !== e) { if (!out) out = x.slice(); out[i] = y; } });
      return out || x;
    }
    if (x && typeof x === 'object') {
      let out: any = null;
      for (const k of Object.keys(x)) {
        const y = walk(x[k]);
        if (y !== x[k]) { if (!out) out = { ...x }; out[k] = y; }
      }
      return out || x;
    }
    return x;
  };
  return walk(v);
}
